// src/Containers/Payment/Paywall.tsx

import { useState } from 'react';
import { FC } from 'react';
import { Button, Box, TextField, Link } from '@mui/material';
import './paywall.css';
import CloseIcon from '../../assets/images/CloseIcon.png';
import GreenCheckmark from '../../assets/images/GreenCheckmark.png';
import { planPricing, discountPercentages, numTrialDays } from '../../Billing/Constants';
import axios from 'axios';
import WelcomeButton from '../TrialWelcome/WelcomeButton';
import LogOutButton from 'Components/LogOutButton';
import { SURVEY_LINK } from 'Utils/Constants';
import { useSelector } from 'react-redux';
import { isCapacitorNative } from 'Utils/Constants';
import { MainAppReducer } from 'Types';
import { stripePriceIds } from '../../Billing/Constants';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'Reducers';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import {
  armIAPReceiptVerification,
  clearIAPReceiptVerificationArm,
  ensureIAPReady,
  forceIAPProductRefresh,
  iapProductIdForPlan,
  iapProductStates,
  iapStoreDisplayName,
  nativeIAPStorePlatform,
} from 'iap/initPurchases';
import type { NativeIAPStorePlatform } from 'iap/initPurchases';
import type { CdvStoreError } from 'iap/initPurchases';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

// Backend endpoint validates backend hard coded list

interface PaywallProps {
  setShowPaywall: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  setPlanTypeSelected: React.Dispatch<React.SetStateAction<string>>;
  exitAllowed: boolean;
  discountCodeValid: boolean | null;
  setDiscountCodeValid: React.Dispatch<React.SetStateAction<boolean | null>>;
  discountCode: string;
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>;
  showDiscountCode: boolean;
  setShowDiscountCode: React.Dispatch<React.SetStateAction<boolean>>;
}

const iapErrorMessage = (error: unknown, storeName: string) => {
  const err = error as { message?: string };
  return err?.message || `${storeName} in-app purchase is unavailable. Please try again.`;
};

const purchaseWithNativeIAP = async (
  planSelect: string,
  iapStorePlatform: NativeIAPStorePlatform,
  dispatch: AppDispatch,
  userData: MainAppReducer['mainAppReducer']['userData'],
  currentAuthToken: () => Promise<string | undefined>,
) => {
  const storeName = iapStoreDisplayName(iapStorePlatform);
  const id = iapProductIdForPlan(planSelect, iapStorePlatform);
  if (!id) {
    throw new Error(`Unknown ${storeName} subscription plan.`);
  }

  const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
  const storePlatform =
    iapStorePlatform === 'apple' ? Cdv?.Platform.APPLE_APPSTORE : Cdv?.Platform.GOOGLE_PLAY;
  if (!Cdv?.store || !storePlatform) {
    throw new Error(
      `${storeName} in-app purchases are still loading. Please wait a moment and try again.`,
    );
  }
  const { store } = Cdv;

  // Ensure products are loaded
  let product = store.get(id, storePlatform);
  if (!product) {
    await forceIAPProductRefresh(Cdv);
    product = store.get(id, storePlatform);
  }
  if (!product) {
    const states = iapProductStates(Cdv, iapStorePlatform);
    console.error(`${storeName} IAP product was not returned by the native store:`, {
      requestedProductId: id,
      productStates: states,
      lastProductLoadErrors: window.__iapLastProductLoadErrors,
    });
    throw new Error(
      `${storeName} did not return the MuseFlow subscription products. Please try again in a few minutes.`,
    );
  }
  if (!product?.canPurchase) {
    await forceIAPProductRefresh(Cdv);
    product = store.get(id, storePlatform);
  }
  if (!product?.canPurchase) {
    throw new Error(`${storeName} product ${id} is not purchasable right now.`);
  }

  const offer = product.getOffer?.();
  if (!offer) {
    throw new Error(`No ${storeName} purchase offer was found for ${id}.`);
  }

  armIAPReceiptVerification(id);
  const err = (await store.order(offer)) as CdvStoreError | undefined;
  if (err) {
    clearIAPReceiptVerificationArm();
    throw new Error(err.message || `${storeName} order failed for ${id}.`);
  }
};

const Paywall: FC<PaywallProps> = ({
  setShowPaywall,
  setShowPayment,
  setPlanTypeSelected: _setPlanTypeSelected,
  exitAllowed,
  discountCodeValid,
  setDiscountCodeValid,
  discountCode,
  setDiscountCode,
  showDiscountCode,
  setShowDiscountCode,
}) => {
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const userData = data.userData;
  const dispatch = useDispatch();
  const { currentAuthToken } = useAuthWrapperContext();

  const [isFreePlan, setIsFreePlan] = useState(false);
  const [iapError, setIapError] = useState<string | null>(null);
  const [purchasingPlan, setPurchasingPlan] = useState<string | null>(null);

  // console.log("userData?", userData)
  const validateDiscountCode = async () => {
    try {
      const authToken = await currentAuthToken();
      await axios.post<{ valid?: boolean }>(
        `${baseUrl}stripe/validate-coupon-code`,
        {
          code: discountCode,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setDiscountCodeValid(true);
      // Now that we know it's valid, check if the discount code is for the 100% off plan (Note the coupon code value must end in a "100")
      if (discountCode && discountCode.slice(-3) == '100') {
        setIsFreePlan(true);
      } else {
        setIsFreePlan(false);
      }
    } catch {
      // if (data.errors && data.errors.code) {
      // console.log("Error validating code!", data.error);
      setDiscountCodeValid(false);
      setIsFreePlan(false);
      // } else {
      //   setDiscountCodeValid(true)
      // }
    }
  };

  let inputClass;
  if (discountCodeValid) {
    inputClass = 'discount-input valid-discount';
  } else if (discountCodeValid == false) {
    inputClass = 'discount-input invalid-discount';
  } else {
    inputClass = 'discount-input discount-input-neutral';
  }

  let monthlyPrice = planPricing.monthly.regular;
  let threeMonthsPrice = planPricing.threeMonths.regular;
  let annuallyPrice = planPricing.annually.regular;
  let monthlyDiscount = discountPercentages.monthly.regular as number | null;
  let threeMonthsDiscount = discountPercentages.threeMonths.regular;
  let annuallyDiscount = discountPercentages.annually.regular;
  if (discountCodeValid) {
    const is84Discount = discountCode.includes('84');
    const is69Discount = discountCode.includes('69');
    const is30Discount = discountCode.includes('30');
    if (is84Discount) {
      monthlyPrice = planPricing.monthly.with84;
      threeMonthsPrice = planPricing.threeMonths.with84;
      annuallyPrice = planPricing.annually.with84;
      monthlyDiscount = discountPercentages.monthly.with84;
      threeMonthsDiscount = discountPercentages.threeMonths.with84;
      annuallyDiscount = discountPercentages.annually.with84;
    } else if (is69Discount) {
      monthlyPrice = planPricing.monthly.with69;
      threeMonthsPrice = planPricing.threeMonths.with69;
      annuallyPrice = planPricing.annually.with69;
      monthlyDiscount = discountPercentages.monthly.with69;
      threeMonthsDiscount = discountPercentages.threeMonths.with69;
      annuallyDiscount = discountPercentages.annually.with69;
    } else if (is30Discount) {
      monthlyPrice = planPricing.monthly.with30;
      threeMonthsPrice = planPricing.threeMonths.with30;
      annuallyPrice = planPricing.annually.with30;
      monthlyDiscount = discountPercentages.monthly.with30;
      threeMonthsDiscount = discountPercentages.threeMonths.with30;
      annuallyDiscount = discountPercentages.annually.with30;
    } else {
      monthlyPrice = planPricing.monthly.withCode;
      threeMonthsPrice = planPricing.threeMonths.withCode;
      annuallyPrice = planPricing.annually.withCode;
      monthlyDiscount = discountPercentages.monthly.withCode;
      threeMonthsDiscount = discountPercentages.threeMonths.withCode;
      annuallyDiscount = discountPercentages.annually.withCode;
    }
  }

  const submitPlanSelect = async (planSelect: string) => {
    // When we wanted to redirect to the payment page::
    // setPlanTypeSelected(planSelect)
    // setShowPaywall(false)
    // setShowPayment(true)

    const iapStorePlatform = nativeIAPStorePlatform();
    if (iapStorePlatform) {
      const storeName = iapStoreDisplayName(iapStorePlatform);
      setIapError(null);
      setPurchasingPlan(planSelect);
      try {
        await purchaseWithNativeIAP(
          planSelect,
          iapStorePlatform,
          dispatch,
          userData,
          currentAuthToken,
        );
      } catch (error) {
        console.error(`${storeName} IAP purchase failed:`, error);
        setIapError(iapErrorMessage(error, storeName));
      } finally {
        setPurchasingPlan(null);
      }
    } else {
      // Now we want to send right to the stripe checkout.
      const authToken = await currentAuthToken();
      const response = await axios.post<{ url?: string; error?: unknown }>(
        `${baseUrl}stripe/checkout-session`,
        {
          customerId: userData!.stripe_customer_id,
          plan: planSelect,
          code: discountCodeValid ? discountCode : null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const data = response.data;
      // Redirect to the url
      if (data.error || !data.url) {
        console.log('Error Submitting payment!', data.error);
        return;
      }
      window.location.href = data.url; // Then redirect to the url created by the checkout session
    }
  };

  return (
    <>
      <Box className="modal-outer" />
      <Box className="modal-inner2 paywall-modal">
        {exitAllowed ? (
          <Box
            sx={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              '&:hover': { cursor: 'pointer' },
            }}
            onClick={() => {
              setShowPaywall(false);
            }}
          >
            <img src={CloseIcon} />
          </Box>
        ) : (
          // if not allowed to bypass paywall; can return to tutorials or roadmap
          // Actually; no back either on paywall
          // <IntroFlowBack/>
          <Box></Box>
        )}
        <Box className="paywall-desc-container">
          <h3 className="paywall-desc-title">Your free trial has ended.</h3>
          <p className="paywall-desc-text" style={{ marginTop: '5px', lineHeight: '133%' }}>
            {' '}
            We hope you enjoyed your {numTrialDays}-day free trial! Please select a payment plan to
            continue playing. We’d also love to hear{' '}
            <Link href={SURVEY_LINK} target="_blank" className="link">
              any feedback you have
            </Link>
            .{' '}
          </p>
        </Box>

        {isFreePlan ? (
          <FreePlan
            discountCode={discountCode}
            setShowPayment={setShowPayment}
            setShowPaywall={setShowPaywall}
          />
        ) : (
          <Box className="plan-container">
            <Box className="plan">
              {discountCodeValid && (
                <Box className="discount-percent">
                  <p className="discount-percent-text">{`${monthlyDiscount}% off`}</p>
                </Box>
              )}
              <Box className="plan-card">
                <Box className="plan-title">
                  <p className="plan-text-desc">Monthly Plan</p>
                  <p className="plan-text-price">{`$${monthlyPrice.toFixed(2)}`}</p>
                  <p className="plan-text-time">per month</p>
                </Box>
                <WelcomeButton
                  className="plan-select-button"
                  onClick={() => submitPlanSelect('monthly')}
                  disabled={purchasingPlan !== null}
                >
                  {purchasingPlan === 'monthly' ? 'LOADING...' : 'SELECT'}
                </WelcomeButton>
              </Box>
            </Box>
            <Box className="plan">
              <Box className="discount-percent">
                <p className="discount-percent-text">{`${threeMonthsDiscount}% off`}</p>
              </Box>
              <Box className="plan-card">
                <Box className="plan-title">
                  <p className="plan-text-desc">3-Month Plan</p>
                  <p className="plan-text-price">{`$${threeMonthsPrice.toFixed(2)}`}</p>
                  <Box>
                    <p className="plan-text-time">per 3 months</p>
                    {!discountCodeValid && (
                      <p className="plan-text-equivalent">$16.67 per month equivalent</p>
                    )}
                  </Box>
                  <Box className="lesson-check" sx={{ width: 'unset' }}>
                    <img src={GreenCheckmark} className="green-check-img" />
                    <p className="plan-text-time">30min lesson</p>
                  </Box>
                </Box>
                <WelcomeButton
                  className="plan-select-button"
                  onClick={() => submitPlanSelect('3-months')}
                  disabled={purchasingPlan !== null}
                >
                  {purchasingPlan === '3-months' ? 'LOADING...' : 'SELECT'}
                </WelcomeButton>
              </Box>
            </Box>
            <Box className="plan">
              <Box className="discount-percent">
                <p className="discount-percent-text">{`${annuallyDiscount}% off`}</p>
              </Box>
              <Box className="plan-card">
                <Box className="plan-title">
                  <p className="plan-text-desc">Annual Plan</p>
                  <p className="plan-text-price">{`$${annuallyPrice.toFixed(2)}`}</p>
                  <Box>
                    <p className="plan-text-time">per 12 months</p>
                    {!discountCodeValid && (
                      <p className="plan-text-equivalent">$15.84 per month equivalent</p>
                    )}
                  </Box>
                  <Box className="lesson-check">
                    <img src={GreenCheckmark} className="green-check-img" />
                    <p className="plan-text-time">Two 30min lessons</p>
                  </Box>
                  <Box className="lesson-check">
                    <img src={GreenCheckmark} className="green-check-img" />
                    <p className="plan-text-time">MIDI cable</p>
                  </Box>
                </Box>
                <WelcomeButton
                  className="plan-select-button"
                  onClick={() => submitPlanSelect('annual')}
                  disabled={purchasingPlan !== null}
                >
                  {purchasingPlan === 'annual' ? 'LOADING...' : 'SELECT'}
                </WelcomeButton>
              </Box>
            </Box>
          </Box>
        )}

        {iapError && (
          <Box className="iap-error" role="alert">
            {iapError}
          </Box>
        )}

        {!isCapacitorNative() && (
          <>
            {!showDiscountCode && (
              <Box className="submit-container">
                {/* <Button onClick={() => navigate('/roadmap')} className="submit-button" variant="contained">Start 14 Day Free Trial</Button> */}
                <Button onClick={() => setShowDiscountCode(true)} className="discount-code">
                  Got a coupon code?
                </Button>
              </Box>
            )}
            {showDiscountCode && (
              <Box className="discount-container">
                <Box className="code-field-container">
                  <TextField
                    className={inputClass}
                    label="Enter Coupon Code"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setDiscountCodeValid(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        validateDiscountCode();
                      }
                    }}
                  />
                  {discountCodeValid ? (
                    <Box className="code-container">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="13"
                        viewBox="0 0 12 13"
                        fill="none"
                      >
                        <path
                          d="M6 1.37158C3.25 1.37158 1 3.62158 1 6.37158C1 9.12158 3.25 11.3716 6 11.3716C8.75 11.3716 11 9.12158 11 6.37158C11 3.62158 8.75 1.37158 6 1.37158ZM5 8.87158L2.5 6.37158L3.205 5.66658L5 7.45658L8.795 3.66158L9.5 4.37158L5 8.87158Z"
                          fill="#2E5B3E"
                        />
                      </svg>
                      CODE APPLIED
                    </Box>
                  ) : (
                    discountCodeValid == false && (
                      <Box className="code-container invalid">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="13"
                          viewBox="0 0 12 13"
                          fill="none"
                        >
                          <path
                            d="M6.5 6.87158H5.5V3.87158H6.5M6.5 8.87158H5.5V7.87158H6.5M6 1.37158C5.34339 1.37158 4.69321 1.50091 4.08658 1.75218C3.47995 2.00346 2.92876 2.37176 2.46447 2.83605C1.52678 3.77373 1 5.0455 1 6.37158C1 7.69766 1.52678 8.96943 2.46447 9.90712C2.92876 10.3714 3.47995 10.7397 4.08658 10.991C4.69321 11.2423 5.34339 11.3716 6 11.3716C7.32608 11.3716 8.59785 10.8448 9.53553 9.90712C10.4732 8.96943 11 7.69766 11 6.37158C11 5.71497 10.8707 5.06479 10.6194 4.45816C10.3681 3.85154 9.99983 3.30034 9.53553 2.83605C9.07124 2.37176 8.52005 2.00346 7.91342 1.75218C7.30679 1.50091 6.65661 1.37158 6 1.37158Z"
                            fill="#B54520"
                          />
                        </svg>
                        INVALID CODE
                      </Box>
                    )
                  )}
                </Box>
                <Button onClick={() => validateDiscountCode()} className="discount-button">
                  Submit
                </Button>
              </Box>
            )}
          </>
        )}
        <Box className="logout-container">
          <LogOutButton />
        </Box>
      </Box>
    </>
  );
};

interface FreePlanProps {
  discountCode: string;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPaywall: React.Dispatch<React.SetStateAction<boolean>>;
}

const FreePlan: FC<FreePlanProps> = ({ discountCode, setShowPayment, setShowPaywall }) => {
  const { currentAuthToken } = useAuthWrapperContext();
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const userData = data.userData;

  const [_isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    const authToken = await currentAuthToken();
    const response = await axios.patch<{ error?: unknown }>(
      `${baseUrl}stripe/update-plan`,
      {
        customerId: userData!.stripe_customer_id,
        paymentMethodId: null,
        priceId: stripePriceIds.free,
        code: discountCode,
      },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
    const data = response.data;
    if (data.error) {
      console.log('Error starting subscription!', data.error);
    } else {
      setShowPayment(false);
      setShowPaywall(false);
    }
    setIsLoading(false);
  };

  return (
    <>
      <Box className="plan-container">
        <Box className="plan">
          <Box className="discount-percent">
            <p className="discount-percent-text">{`100% off`}</p>
          </Box>
          <Box className="plan-card">
            <Box className="plan-title">
              <p className="plan-text-desc">FREE SUBSCRIPTION</p>
              {/* <p className="plan-text-desc">Teacher Plan</p> */}
              <p className="plan-text-price">$0.00</p>
              <p className="plan-text-time">per month</p>
            </Box>
            <Box className="lesson-check" sx={{ width: 'unset' }}>
              <img src={GreenCheckmark} className="green-check-img" />
              <p className="plan-text-time">Complete Access</p>
            </Box>
            <WelcomeButton className="plan-select-button" onClick={() => handleSubmit()}>
              SELECT
            </WelcomeButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Paywall;
